import classNames from 'classnames';
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

const PULL_THRESHOLD = 72;
const MAX_PULL = 120;
const INDICATOR_HEIGHT = 48;

interface PullToRefreshProps {
    children: ReactNode;
    onRefresh: () => Promise<void> | void;
    className?: string;
    id?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onScroll?: React.UIEventHandler<HTMLDivElement>;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
    children,
    onRefresh,
    className,
    id,
    onClick,
    onScroll,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const pullingRef = useRef(false);
    const pullDistanceRef = useRef(0);
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        pullDistanceRef.current = pullDistance;
    }, [pullDistance]);

    const resetPull = useCallback(() => {
        pullingRef.current = false;
        setPullDistance(0);
    }, []);

    const finishRefresh = useCallback(async () => {
        setRefreshing(true);
        setPullDistance(INDICATOR_HEIGHT);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
            resetPull();
        }
    }, [onRefresh, resetPull]);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const handleTouchStart = (event: TouchEvent) => {
            if (refreshing || scrollEl.scrollTop > 0) return;
            startYRef.current = event.touches[0].clientY;
            pullingRef.current = true;
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (!pullingRef.current || refreshing) return;
            if (scrollEl.scrollTop > 0) {
                resetPull();
                return;
            }

            const delta = event.touches[0].clientY - startYRef.current;
            if (delta <= 0) {
                setPullDistance(0);
                return;
            }

            event.preventDefault();
            setPullDistance(Math.min(delta * 0.45, MAX_PULL));
        };

        const handleTouchEnd = () => {
            if (!pullingRef.current || refreshing) return;

            if (pullDistanceRef.current >= PULL_THRESHOLD) {
                void finishRefresh();
                return;
            }

            resetPull();
        };

        scrollEl.addEventListener('touchstart', handleTouchStart, {
            passive: true,
        });
        scrollEl.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });
        scrollEl.addEventListener('touchend', handleTouchEnd);
        scrollEl.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            scrollEl.removeEventListener('touchstart', handleTouchStart);
            scrollEl.removeEventListener('touchmove', handleTouchMove);
            scrollEl.removeEventListener('touchend', handleTouchEnd);
            scrollEl.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [finishRefresh, refreshing, resetPull]);

    const indicatorHeight = refreshing
        ? INDICATOR_HEIGHT
        : Math.max(pullDistance, 0);

    return (
        <div className="pull-to-refresh">
            <div
                className="pull-to-refresh__indicator"
                style={{ height: indicatorHeight }}
                aria-hidden={indicatorHeight === 0}
            >
                {(refreshing || pullDistance > 0) && (
                    <div
                        className={classNames('loader', {
                            'pull-to-refresh__loader--active': refreshing,
                        })}
                    />
                )}
            </div>
            <div
                ref={scrollRef}
                className={classNames('pull-to-refresh__content', className)}
                id={id}
                onClick={onClick}
                onScroll={onScroll}
            >
                {children}
            </div>
        </div>
    );
};

export default PullToRefresh;
